import Phaser from 'phaser';
import { ShapeBody } from './shapeFactory';
import { physicsRadiusFor, TIERS, getTierScale } from './shapes';

const DWELL_MS = 3000;
// Matter velocities in Phaser are in pixels-per-step; settled bodies are < ~0.2.
// Use a generous threshold so mid-fall bodies never count as resting.
const MAX_VEL = 0.5;
// Grace period (ms) before a newly-dropped body is eligible for the dead-line check.
// Otherwise a freshly-released body at y=64 with zero velocity will start the timer
// before gravity has had a chance to accelerate it past MAX_VEL.
const GRACE_MS = 800;

/**
 * Monitors all dynamic shape bodies. If any body's top edge stays above the
 * dead line for >= DWELL_MS with low velocity, fires onGameOver.
 *
 * Bodies with high velocity (e.g. mid-bounce or just-spawned) are forgiven.
 */
export class DeadLine {
  private scene: Phaser.Scene;
  private y: number;
  private timers = new Map<number, number>();
  onGameOver?: () => void;
  enabled = true;
  visual: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, y: number, leftX: number, rightX: number) {
    this.scene = scene;
    this.y = y;
    this.visual = scene.add.graphics();
    this.visual.setDepth(5);
    this.drawLine(leftX, rightX);
  }

  private drawLine(leftX: number, rightX: number): void {
    // Subtle warning band rather than a loud line. A soft inner stripe plus a
    // thinner, lower-opacity dashed top edge reads as "danger zone" without
    // shouting over the rest of the UI.
    this.visual.clear();
    // Soft fade band hugging the line — a couple of px tall.
    this.visual.fillStyle(0xff4757, 0.07);
    this.visual.fillRect(leftX, this.y - 2, rightX - leftX, 6);
    // Thin dashed top edge.
    this.visual.lineStyle(1, 0xff4757, 0.45);
    const dashLen = 6;
    const gapLen = 5;
    let x = leftX;
    while (x < rightX) {
      this.visual.beginPath();
      this.visual.moveTo(x, this.y);
      this.visual.lineTo(Math.min(x + dashLen, rightX), this.y);
      this.visual.strokePath();
      x += dashLen + gapLen;
    }
  }

  destroy(): void {
    this.visual.destroy();
  }

  /**
   * Each entry: shape id -> { firstSeen: when added to dynamicBodies; aboveSince: when started resting above the line }.
   */
  private droppedAt = new Map<number, number>();

  /**
   * Call each tick with the current set of dynamic shape bodies (i.e. those
   * dropped from the spawner; the currently-held shape at the top should be
   * excluded).
   */
  update(now: number, bodies: ShapeBody[]): void {
    if (!this.enabled) return;
    const seen = new Set<number>();
    for (const body of bodies) {
      if (!body.active || body.merging) continue;
      seen.add(body.shapeId);
      // Skip newly-dropped bodies during grace period to avoid the y=64 spawn position triggering instantly.
      if (!this.droppedAt.has(body.shapeId)) this.droppedAt.set(body.shapeId, now);
      if (now - (this.droppedAt.get(body.shapeId) ?? now) < GRACE_MS) continue;

      // Use the physics radius (the actual collider) rather than the visual
      // bounding radius. The body's "top edge" for the dead-line check should
      // match where the physics actually sits, not where the texture extends.
      const tier = TIERS[body.shapeTier];
      const radius = physicsRadiusFor(tier.kind, tier.radius * getTierScale());
      const top = body.y - radius;
      const matter = body.body as MatterJS.BodyType;
      const speed = Math.hypot(matter.velocity.x, matter.velocity.y);
      if (top < this.y && speed < MAX_VEL) {
        const since = this.timers.get(body.shapeId) ?? now;
        this.timers.set(body.shapeId, since);
        if (now - since >= DWELL_MS) {
          this.enabled = false;
          this.onGameOver?.();
          return;
        }
      } else {
        this.timers.delete(body.shapeId);
      }
    }
    // Clean up timers/grace for bodies no longer present.
    for (const id of [...this.timers.keys()]) if (!seen.has(id)) this.timers.delete(id);
    for (const id of [...this.droppedAt.keys()]) if (!seen.has(id)) this.droppedAt.delete(id);
  }
}
